import { SectionErrorBoundary } from "@/components/SectionErrorBoundary";
import { usePlugins } from "./PluginContext";

/**
 * Renders a plugin-contributed page: resolves the component registered for
 * (pluginId, path), passes the plugin's SDK, and contains crashes so a broken
 * plugin can never take down the app shell.
 */
export function PluginPageRenderer({ pluginId, path }: { pluginId: string; path: string }) {
  const { getLoadedPlugin, loadErrors } = usePlugins();
  const loadedPlugin = getLoadedPlugin(pluginId);

  if (!loadedPlugin) {
    const error = loadErrors[pluginId];
    return (
      <div className="flex h-full items-center justify-center p-8 text-center">
        <div>
          <h2 className="text-lg font-semibold">
            {error ? "Plugin failed to load" : "Loading plugin…"}
          </h2>
          {error && <p className="mt-2 text-sm text-muted-foreground">{error}</p>}
        </div>
      </div>
    );
  }

  const PageComponent = loadedPlugin.registration.pages[path];
  if (!PageComponent) {
    return (
      <div className="flex h-full items-center justify-center p-8 text-center">
        <div>
          <h2 className="text-lg font-semibold">Page not found in plugin</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            “{loadedPlugin.plugin.name}” did not register a component for {path}
          </p>
        </div>
      </div>
    );
  }

  return (
    <SectionErrorBoundary sectionName={`Plugin: ${loadedPlugin.plugin.name}`}>
      <div id={`plugin-root-${pluginId}`} className="h-full min-h-0 w-full overflow-hidden">
        <PageComponent sdk={loadedPlugin.sdk} />
      </div>
    </SectionErrorBoundary>
  );
}
